import { Anchor, Box, Button, Card, CardBody, CardFooter, Text } from 'grommet';
import { Inspect } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUndoableDelete } from 'src/utils/useUndoableDelete';
import {
  AddNewButton,
  BulkDeleteButton,
  CommercialEditReviewModal,
  ConfirmModal,
  Divider,
  EditableText,
  StatusPill,
  tableHeaderTextProps,
} from '../..';
import { CommercialCardHeader } from '../commercials/CommercialCardHeader';

import {
  Commercial,
  CommercialType,
  Edit,
  EditType,
  HoldingFeeCycleStatus,
  TCommercialId,
  TEditId,
  THoldingFeeCycleInfo,
  TUpdateCommercialRequest,
  TUpdateEditRequest,
} from '@talon/web/lib/api';
import {
  useCommercialStore,
  useEstimateStore,
  useHoldingFeeStore,
  usePerformerStore,
  useProjectStore,
} from '@talon/web/src/context';
import { USD } from 'src/utils/currency';
import { formatDateOnly } from 'src/utils/dateOnly';
import { CreateEditModal } from './CreateEditModal';
import { EditRow } from './EditRow';
import { EmptyState } from '@talon/web/src/components/ui/EmptyState';

export type CommercialEditRowProps = {
  commercialId: TCommercialId;
  onChecked: () => void;
  checked: boolean;
  initialEditId?: string;
};

export const CommercialEditRow = observer(
  ({ commercialId, onChecked, checked, initialEditId }: CommercialEditRowProps) => {
    /** Context **/
    const navigate = useNavigate();
    const estimateStore = useEstimateStore();
    const commercialStore = useCommercialStore();
    const performerStore = usePerformerStore();
    const holdingFeeStore = useHoldingFeeStore();
    const projectStore = useProjectStore();

    /** State **/
    const [commercial, setCommercial] = useState<Commercial>();
    const [showAddEditModal, setShowAddEditModal] = useState(false);
    const [pendingTypeChange, setPendingTypeChange] = useState<CommercialType>();
    const [selectedEditIds, setSelectedEditIds] = useState<TEditId[]>([]);
    const [hiddenEditIds, setHiddenEditIds] = useState<TEditId[]>([]);
    const deleteWithUndo = useUndoableDelete();
    const [showCommercialEditReviewModal, setShowCommercialEditReviewModal] = useState(false);
    const [wages, setWages] = useState(0);
    const [total, setTotal] = useState(0);
    const [expandedHoldingFeesContractId, setExpandedHoldingFeesContractId] = useState<string | null>(null);
    const [holdingFeeSchedules, setHoldingFeeSchedules] = useState<Record<string, THoldingFeeCycleInfo[]>>({});

    /** Refs **/
    const columnWidths = [
      '48px', // checkbox
      '21%', // name
      '20%', // type
      '8%', // duration
      '6%', // paid usage
      '19%', // adid/isci
      '224px', // asset
      '48px', // edit reviews button
    ];

    /** Computed **/
    const usageEstimate = estimateStore.getTempUsageEstimate(commercial?.id);

    /** Methods **/

    // Reads estimateStore.tempEstimate directly (rather than a value captured during
    // render) so this isn't tracked as a render-time MobX dependency - otherwise every
    // commercial's row re-renders whenever any one of them recalculates its estimate.
    const recalculateUsageEstimate = async () => {
      if (!commercial) return;

      const usageEstimate = estimateStore.tempEstimate?.usage_estimates.find(
        (usageEstimate) => usageEstimate.commercial?.id === commercial.id
      );

      if (usageEstimate) await estimateStore.updateUsageEstimate(usageEstimate.id, undefined, true);
    };

    const chooseEdit = (id: TEditId) => {
      if (selectedEditIds.includes(id)) {
        setSelectedEditIds(selectedEditIds.filter((s) => s !== id));
      } else {
        setSelectedEditIds([...selectedEditIds, id]);
      }
    };

    const updateCommercial = async (update: TUpdateCommercialRequest) => {
      if (!commercial) return;
      const updatedCommercial = await commercialStore.updateCommercial(commercial.id, update);

      if (updatedCommercial) {
        setCommercial(updatedCommercial);

        // Only the commercial's type affects usage fee calculations (changing it already
        // warns about resetting usages, below) - title/duration edits don't, so skip the
        // costly whole-project estimate refresh for those.
        if (update.type !== undefined) await recalculateUsageEstimate();
      }
    };

    const onChangeCommercialType = async (type: CommercialType) =>
      type !== commercial?.type && commercial?.usages.length
        ? setPendingTypeChange(type)
        : await updateCommercial({ type: type });

    const onConfirmCommercialType = async (type: CommercialType) => {
      await updateCommercial({ type });
      setPendingTypeChange(undefined);
    };

    const addEdit = async (commercialId: TCommercialId, editType: EditType) => {
      if (!commercial) return;
      const edit = await commercialStore.createEdit({
        title: `${commercial.title} ${editType} Edit`,
        type: editType,
        commercialId,
        durationInSeconds: 0, // Default duration, can be updated later
      });
      if (edit && commercial) {
        const edits = [...commercial.edits, edit];
        const updatedCommercial = { ...commercial, edits };
        setCommercial(updatedCommercial as Commercial);
      }
      setShowAddEditModal(false);
    };

    const updateEdit = useCallback(
      async (update: Edit) => {
        if (!commercial) return;
        console.log('Updating edit:', update);

        // Build the update request, explicitly including undefined values as null for the API
        const updateRequest: TUpdateEditRequest = {};
        if (update.duration !== undefined) updateRequest.durationInSeconds = update.duration;
        if (update.ad_id !== undefined) updateRequest.adId = update.ad_id;
        if ('asset_id' in update) updateRequest.assetId = update.asset_id || null;
        if (update.is_paid !== undefined) updateRequest.isPaid = update.is_paid;
        if (update.title !== undefined) updateRequest.title = update.title;
        if (update.type !== undefined) updateRequest.type = update.type;

        const edit = await commercialStore.updateEdit(update.id, updateRequest);
        if (!edit) return;
        const edits = commercial.edits?.map((e) => (e.id === edit.id ? edit : e));
        const updatedCommercial = { ...commercial, edits };
        setCommercial(updatedCommercial);
      },
      [commercial, commercialStore]
    );

    const deleteEdits = async () => {
      if (!commercial || selectedEditIds.length === 0) return;

      const deleted: string[] = [];
      for (const id of selectedEditIds) {
        if (await commercialStore.deleteEdit(id)) deleted.push(id);
      }
      if (!deleted.length) return;

      const edits = commercial.edits.filter((edit) => !deleted.includes(edit.id));
      setCommercial({ ...commercial, edits });
      setSelectedEditIds([]);
    };

    const getUsageConsumedForRole = (roleId?: string): number => {
      if (!roleId || !usageEstimate) return 0;
      return usageEstimate.items
        .filter((item) => item.role?.id === roleId)
        .reduce((sum, item) => sum + item.subtotal, 0);
    };

    const guaranteeRollup = (commercial?.contracts ?? []).reduce(
      (acc, contract) => {
        const guarantee = contract.usage_guarantee_amount;
        if (!guarantee) return acc;

        const overage = getUsageConsumedForRole(contract.role?.id) - guarantee;
        if (overage > 0) {
          acc.performersOverGuarantee += 1;
          acc.totalOverage += overage;
        }
        return acc;
      },
      { performersOverGuarantee: 0, totalOverage: 0 }
    );

    const toggleHoldingFeeSchedule = async (contractId: string) => {
      if (expandedHoldingFeesContractId === contractId) {
        setExpandedHoldingFeesContractId(null);
        return;
      }

      setExpandedHoldingFeesContractId(contractId);
      if (!holdingFeeSchedules[contractId]) {
        const schedule = await holdingFeeStore.getSchedule(contractId);
        if (schedule) {
          setHoldingFeeSchedules((prev) => ({ ...prev, [contractId]: schedule.cycles }));
        }
      }
    };

    const updateHoldingFeeCycle = async (contractId: string, cycleNumber: number, status: HoldingFeeCycleStatus) => {
      const updatedCycle = await holdingFeeStore.updateCycle(contractId, cycleNumber, status);
      if (!updatedCycle) return;

      setHoldingFeeSchedules((prev) => ({
        ...prev,
        [contractId]: (prev[contractId] ?? []).map((cycle) =>
          cycle.cycleNumber === cycleNumber ? updatedCycle : cycle
        ),
      }));
    };

    const updateGuaranteeAmount = async (contractId: string, amount: number) => {
      if (!commercial) return;
      const updatedContract = await performerStore.updateUsageGuaranteeAmount(contractId, amount);
      if (!updatedContract) return;

      const contracts = commercial.contracts?.map((c) =>
        c.id === contractId ? { ...c, usage_guarantee_amount: amount } : c
      );
      setCommercial({ ...commercial, contracts });
    };

    /** Effects **/

    useEffect(() => {
      const getCommercial = async (id: TCommercialId) => {
        if (!projectStore.currentProject) return;
        const c = await commercialStore.getCommercial(id);
        if (c) setCommercial(c);
      };

      getCommercial(commercialId);
      // Depend on the project id, not the currentProject object itself - that object gets a
      // new reference on every enrichment/update, which would otherwise re-fetch this
      // commercial (and cascade into every row re-rendering) far more often than needed.
    }, [commercialId, projectStore.currentProject?.id]);

    // Auto-open edit review modal when initialEditId is provided (from notification)
    useEffect(() => {
      if (initialEditId && commercial) {
        setShowCommercialEditReviewModal(true);
      }
    }, [initialEditId, commercial]);

    useEffect(() => {
      if (usageEstimate) {
        let wages = 0;
        let total = 0;

        for (const item of usageEstimate.items) {
          wages += item.subtotal;
          total += item.subtotal_plus_fringe;
        }

        setWages(wages);
        setTotal(total);
      }
    }, [usageEstimate]);

    /** Render **/

    return (
      <Card round="10px" border={{ color: 'border-card' }} elevation="none" background="white">
        {/* Header */}
        <CommercialCardHeader
          commercial={commercial}
          checked={checked}
          onChecked={onChecked}
          onTitle={(title) => updateCommercial({ title })}
          onType={onChangeCommercialType}
          onDuration={(durationInSeconds) => updateCommercial({ durationInSeconds })}
          summary={`${commercial?.edits?.length ?? 0} ${commercial?.edits?.length === 1 ? 'edit' : 'edits'}`}
          wages={wages}
          total={total}
        />

        {/* MPU / Session Info */}
        {commercial?.last_shoot_date && (
          <Box
            direction="row"
            gap="medium"
            pad={{ vertical: 'xsmall', horizontal: 'small' }}
            background="light-3"
            border={{ side: 'bottom', size: '1px', color: 'light-4' }}
          >
            <Text size="xsmall" color="dark-4">
              Last Shoot Date: {formatDateOnly(commercial.last_shoot_date)}
            </Text>
            {commercial.mpu_start_date && commercial.mpu_end_date && (
              <Text size="xsmall" color="dark-4">
                MPU Window: {formatDateOnly(commercial.mpu_start_date)} –{' '}
                {formatDateOnly(commercial.mpu_end_date)}
              </Text>
            )}
          </Box>
        )}

        {/* Body */}
        <CardBody background="white">
          {/* Table Header */}
          <Box
            direction="row"
            justify="between"
            align="center"
            gap="medium"
            pad={{ vertical: 'xsmall', horizontal: 'small' }}
            background="surface-header"
            border={{ side: 'bottom', color: 'border-card' }}
          >
            <Box height="1rem" width={columnWidths[0]} />

            <Box width={columnWidths[1]}>
              <Text {...tableHeaderTextProps}>Edit name</Text>
            </Box>
            <Box width={columnWidths[2]}>
              <Text {...tableHeaderTextProps}>Edit type</Text>
            </Box>
            <Box width={columnWidths[3]}>
              <Text {...tableHeaderTextProps}>Duration (sec)</Text>
            </Box>
            <Box width={columnWidths[4]} align="center">
              <Text {...tableHeaderTextProps}>Paid usage</Text>
            </Box>
            <Box width={columnWidths[5]}>
              <Text {...tableHeaderTextProps}>Ad ID / ISCI</Text>
            </Box>
            <Box width={columnWidths[6]}>{/* <Text size="xsmall">ASSET</Text> */}</Box>

            <Box height="1rem" width={columnWidths[7]} />
            <Box height="1rem" width="40px" flex={false} />
          </Box>

          {(commercial?.edits ?? []).some((edit) => !hiddenEditIds.includes(edit.id)) ? (
            commercial?.edits?.filter((edit) => !hiddenEditIds.includes(edit.id)).map((edit, i) => (
              <Box key={edit.id}>
                {i > 0 && <Divider height="0.5px" color="light-3" />}
                <EditRow
                  edit={edit}
                  commercial={commercial}
                  onChecked={() => chooseEdit(edit.id)}
                  checked={selectedEditIds.includes(edit.id)}
                  onDelete={() =>
                    deleteWithUndo({
                      title: edit.title || 'Edit',
                      hide: () => {
                        setHiddenEditIds((ids) => [...ids, edit.id]);
                        setSelectedEditIds((ids) => ids.filter((id) => id !== edit.id));
                      },
                      restore: () => setHiddenEditIds((ids) => ids.filter((id) => id !== edit.id)),
                      commit: async () => {
                        const deleted = await commercialStore.deleteEdit(edit.id);
                        if (!deleted) return false;
                        setCommercial((current) => current && {
                          ...current,
                          edits: current.edits.filter((item) => item.id !== edit.id),
                        });
                        setHiddenEditIds((ids) => ids.filter((id) => id !== edit.id));
                        return true;
                      },
                    })
                  }
                  updateEdit={(update) => updateEdit({ ...edit, ...update })}
                  columnWidths={columnWidths}
                />
              </Box>
            ))
          ) : (
            <EmptyState
              title="No edits yet"
              body="Add each cut of this spot to track its cast and usage."
              actionLabel="Add edit"
              onAction={() => setShowAddEditModal(true)}
            />
          )}

          {/* Cast */}
          <Box
            direction="row"
            align="center"
            justify="between"
            gap="medium"
            pad={{ vertical: 'xsmall', horizontal: 'small' }}
            background="surface-header"
            border={{ side: 'bottom', color: 'border-card' }}
          >
            <Text {...tableHeaderTextProps}>Cast</Text>
            {guaranteeRollup.performersOverGuarantee > 0 && (
              <Text size="xsmall" color="status-critical" weight="bold">
                {guaranteeRollup.performersOverGuarantee} performer
                {guaranteeRollup.performersOverGuarantee > 1 ? 's' : ''} over usage guarantee (
                {USD(guaranteeRollup.totalOverage)} total)
              </Text>
            )}
          </Box>

          {(commercial?.contracts ?? []).length > 0 ? (
            commercial?.contracts?.map((contract, i) => {
              const consumed = getUsageConsumedForRole(contract.role?.id);
              const guarantee = contract.usage_guarantee_amount;
              const overage = guarantee ? consumed - guarantee : 0;

              return (
                <Box key={contract.id}>
                  {i > 0 && <Divider height="0.5px" color="light-3" />}
                  <Box direction="row" align="center" gap="medium" pad={{ vertical: 'xsmall', horizontal: 'small' }}>
                    <Box width={columnWidths[1]}>
                      <Anchor size="small" onClick={() => navigate(`/performers/${contract.performer?.id}`)}>
                        {contract.performer?.first_name} {contract.performer?.last_name}
                      </Anchor>
                    </Box>
                    <Box width={columnWidths[2]}>
                      <Text size="small" color="dark-3">
                        {contract.role?.name}
                        {contract.role?.performer_type ? ` (${contract.role.performer_type})` : ''}
                      </Text>
                    </Box>
                    <Box direction="row" gap="xsmall" align="center">
                      <Text size="small">Usage Guarantee:</Text>
                      <EditableText
                        val={guarantee?.toString() ?? ''}
                        onChange={(val) => updateGuaranteeAmount(contract.id, val ? parseFloat(val) : 0)}
                        minValue={0}
                      />
                    </Box>
                    {guarantee ? (
                      overage > 0 ? (
                        <Text size="small" color="status-critical" weight="bold">
                          {USD(overage)} OVER guarantee
                        </Text>
                      ) : (
                        <Text size="small" color="dark-3">
                          {USD(Math.abs(overage))} remaining of guarantee
                        </Text>
                      )
                    ) : null}

                    <Box flex />

                    <Anchor size="small" onClick={() => toggleHoldingFeeSchedule(contract.id)}>
                      Holding Fees
                    </Anchor>
                  </Box>

                  {expandedHoldingFeesContractId === contract.id && (
                    <Box
                      pad={{ vertical: 'xsmall', horizontal: 'medium' }}
                      background="light-1"
                      gap="xxsmall"
                      border={{ side: 'top', size: '1px', color: 'light-4' }}
                    >
                      {(holdingFeeSchedules[contract.id] ?? []).map((cycle) => (
                        <Box key={cycle.cycleNumber} direction="row" align="center" gap="small">
                          <Box width="80px">
                            <Text size="xsmall">Cycle {cycle.cycleNumber}</Text>
                          </Box>
                          <Box width="120px">
                            <Text size="xsmall">{formatDateOnly(cycle.dueDate)}</Text>
                          </Box>
                          <Box width="80px">
                            <StatusPill
                              label={cycle.status}
                              tone={
                                cycle.status === 'Overdue'
                                  ? 'danger'
                                  : cycle.status === 'Paid'
                                    ? 'ok'
                                    : cycle.status === 'Due'
                                      ? 'warning'
                                      : 'neutral'
                              }
                            />
                          </Box>
                          {(cycle.status === 'Due' || cycle.status === 'Overdue') && (
                            <Box direction="row" gap="xsmall">
                              <Anchor
                                size="xsmall"
                                onClick={() =>
                                  updateHoldingFeeCycle(contract.id, cycle.cycleNumber, HoldingFeeCycleStatus.PAID)
                                }
                              >
                                Mark Paid
                              </Anchor>
                              <Anchor
                                size="xsmall"
                                onClick={() =>
                                  updateHoldingFeeCycle(contract.id, cycle.cycleNumber, HoldingFeeCycleStatus.WAIVED)
                                }
                              >
                                Waive
                              </Anchor>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })
          ) : (
            <EmptyState title="No cast yet" body="Performers hired for this commercial's roles appear here." />
          )}
        </CardBody>

        {/* Footer */}
        <CardFooter
          justify="between"
          gap="small"
          background="white"
          border={{ side: 'top', color: 'border-divider' }}
          pad={{ vertical: 'small', horizontal: '16px' }}
        >
          <AddNewButton plain label="Add edit" tipText="Add Edit Data" onClick={() => setShowAddEditModal(true)} />
          <Box direction="row" gap="small" align="center">
            <BulkDeleteButton
              count={selectedEditIds.length}
              singular="edit"
              plural="edits"
              names="the selected edits"
              onDelete={deleteEdits}
            />
            {commercial && commercial.edits && commercial.edits.length > 0 && (
              <Button
                secondary
                icon={<Inspect />}
                size="small"
                label="Edit reviews"
                onClick={() => setShowCommercialEditReviewModal(true)}
                tip="View and manage edit reviews for all edits"
              />
            )}
          </Box>
        </CardFooter>

        {commercial && showCommercialEditReviewModal && (
          <CommercialEditReviewModal
            commercial={commercial}
            setShowCommercialEditReviewModal={setShowCommercialEditReviewModal}
            initialEditId={initialEditId}
          />
        )}

        {commercial && showAddEditModal && (
          <CreateEditModal commercial={commercial} addEdit={addEdit} setShowModal={setShowAddEditModal} />
        )}

        {pendingTypeChange && (
          <ConfirmModal
            content="Warning: changing the Commercial type will reset all existing Usages. Continue?"
            danger
            onConfirm={() => onConfirmCommercialType(pendingTypeChange)}
            onCancel={() => setPendingTypeChange(undefined)}
          />
        )}
      </Card>
    );
  }
);
